"use client";

import { c as _c } from "react/compiler-runtime";
import { LayoutGroup } from "framer-motion";
import { usePathname, useSearchParams } from "next/navigation";
import React, { Suspense, useCallback, useMemo, useRef } from "react";
import { getActiveWaveIdFromUrl, getNotificationsRoute } from "@/helpers/navigation.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useWave } from "@/hooks/useWave";
import { useWaveData } from "@/hooks/useWaveData";
import { useLayout } from "../brain/my-stream/layout/LayoutContext";
import BellIcon from "../common/icons/BellIcon";
import ChatBubbleIcon from "../common/icons/ChatBubbleIcon";
import DiscoverIcon from "../common/icons/DiscoverIcon";
import LogoIcon from "../common/icons/LogoIcon";
import CollectionsMenuIcon from "../common/icons/CollectionsMenuIcon";
import UsersIcon from "../common/icons/UsersIcon";
import WavesIcon from "../common/icons/WavesIcon";
import NavItem from "./NavItem";
import type { NavItem as NavItemData } from "./navTypes";
const items: NavItemData[] = [{
  kind: "route",
  name: "Discovery",
  href: "/discover",
  icon: "discover",
  iconComponent: DiscoverIcon
}, {
  kind: "view",
  name: "Waves",
  viewKey: "waves",
  icon: "waves",
  iconComponent: WavesIcon,
  iconSizeClass: "tw-size-6"
}, {
  kind: "view",
  name: "Messages",
  viewKey: "messages",
  icon: "messages",
  iconComponent: ChatBubbleIcon
}, {
  kind: "route",
  name: "Home",
  href: "/",
  icon: "home",
  iconComponent: LogoIcon,
  iconSizeClass: "tw-size-9"
}, {
  kind: "route",
  name: "Network",
  href: "/network",
  icon: "network",
  iconComponent: UsersIcon
}, {
  kind: "route",
  name: "Collections",
  href: "/the-memes",
  icon: "collections",
  iconComponent: CollectionsMenuIcon
}, {
  kind: "route",
  name: "Notifications",
  href: "/notifications",
  icon: "notifications",
  iconComponent: BellIcon
}];
interface BottomNavigationProps {
  readonly hidden?: boolean | undefined;
}
const getHiddenStyle = (hidden: boolean) => hidden ? "tw-opacity-0 tw-translate-y-full tw-pointer-events-none" : "tw-opacity-100 tw-translate-y-0";
const getNavClassName = (hidden: boolean) => `${getHiddenStyle(hidden)} tw-fixed tw-bottom-0 tw-left-0 tw-z-50 tw-h-[85px] tw-w-full tw-bg-black tw-shadow-inner tw-transition-[opacity,transform] tw-duration-75`;
const navInnerClassName = "tw-relative tw-h-full before:tw-absolute before:tw-inset-x-0 before:tw-top-0 before:tw-h-px before:tw-bg-iron-900 before:tw-content-['']";
const navListClassName = "tw-mx-auto tw-flex tw-h-full tw-pl-[env(safe-area-inset-left,0px)] tw-pr-[env(safe-area-inset-right,0px)] md:tw-max-w-2xl";
const BottomNavigationFallback: React.FC<BottomNavigationProps> = t0 => {
  const $ = _c(5);
  const {
    hidden: t1
  } = t0;
  const hidden = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== hidden) {
    t2 = getNavClassName(hidden);
    $[0] = hidden;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className={navInnerClassName}><ul className={navListClassName} /></div>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2) {
    t4 = <nav aria-hidden="true" className={t2}>{t3}</nav>;
    $[3] = t2;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
const BottomNavigationContent: React.FC<BottomNavigationProps> = t0 => {
  const $ = _c(24);
  const {
    hidden: t1
  } = t0;
  const hidden = t1 === undefined ? false : t1;
  const {
    registerRef
  } = useLayout();
  const {
    isApp
  } = useDeviceInfo();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const mobileNavRef = useRef(null);
  let t2;
  if ($[0] !== pathname || $[1] !== searchParams) {
    t2 = getActiveWaveIdFromUrl({
      pathname,
      searchParams
    });
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const waveIdFromQuery = t2;
  let t3;
  if ($[3] !== waveIdFromQuery) {
    t3 = {
      waveId: waveIdFromQuery,
      onWaveNotFound: _temp
    };
    $[3] = waveIdFromQuery;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: waveData
  } = useWaveData(t3);
  const {
    isDm: isCurrentWaveDm
  } = useWave(waveData);
  let t4;
  if ($[5] !== registerRef) {
    t4 = node => {
      mobileNavRef.current = node;
      registerRef("mobileNav", node);
    };
    $[5] = registerRef;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const setMobileNavRef = t4;
  let t5;
  if ($[7] !== isApp) {
    t5 = items.map(item => item.name === "Notifications" ? {
      ...item,
      href: getNotificationsRoute(isApp)
    } : item);
    $[7] = isApp;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const navItems = t5;
  let t6;
  if ($[9] !== hidden) {
    t6 = getNavClassName(hidden);
    $[9] = hidden;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== isApp || $[12] !== isCurrentWaveDm || $[13] !== navItems) {
    let t8;
    if ($[15] !== isApp || $[16] !== isCurrentWaveDm) {
      t8 = item_0 => <li key={item_0.name} className="tw-flex tw-h-full tw-min-w-0 tw-flex-1 tw-items-center tw-justify-center"><NavItem item={item_0} isCurrentWaveDm={isCurrentWaveDm} fullPrefetch={isApp && item_0.kind === "view"} /></li>;
      $[15] = isApp;
      $[16] = isCurrentWaveDm;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    t7 = navItems.map(t8);
    $[11] = isApp;
    $[12] = isCurrentWaveDm;
    $[13] = navItems;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[18] !== t7) {
    t8 = <div className={navInnerClassName}><ul className={navListClassName}><LayoutGroup id="bottom-navigation">{t7}</LayoutGroup></ul></div>;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== setMobileNavRef || $[21] !== t6 || $[22] !== t8) {
    t9 = <nav ref={setMobileNavRef} className={t6}>{t8}</nav>;
    $[20] = setMobileNavRef;
    $[21] = t6;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
};
const BottomNavigation: React.FC<BottomNavigationProps> = t0 => {
  const $ = _c(2);
  const {
    hidden: t1
  } = t0;
  const hidden = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== hidden) {
    t2 = <Suspense fallback={<BottomNavigationFallback hidden={hidden} />}><BottomNavigationContent hidden={hidden} /></Suspense>;
    $[0] = hidden;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
};
export default BottomNavigation;
function _temp() {}